import { Direction as RadixDirection } from 'radix-ui';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useControllableState } from '../lib/hooks';
import type { Dir } from '../lib/types';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
  /** The user's preference (may be `system`). */
  theme: ThemePreference;
  /** What is actually applied (`data-theme`). */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
  /** Flips between light and dark (a `system` preference becomes explicit). */
  toggleTheme: () => void;
  dir: Dir;
  setDir: (dir: Dir) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children?: ReactNode;
  /** Controlled theme preference. */
  theme?: ThemePreference;
  /** Initial preference when uncontrolled (default `system`). */
  defaultTheme?: ThemePreference;
  onThemeChange?: (theme: ThemePreference) => void;
  /** Controlled text direction. */
  dir?: Dir;
  /** Initial direction when uncontrolled (default `ltr`). */
  defaultDir?: Dir;
  onDirChange?: (dir: Dir) => void;
  /** Optional `lang` attribute written next to `dir` (e.g. `ar`). */
  lang?: string;
  /**
   * Element that receives `data-theme` and `dir`. Defaults to `document.documentElement` so that
   * portals (dialogs, menus, toasts) inherit the theme.
   */
  target?: HTMLElement | null | (() => HTMLElement | null);
  /** Persist the preference/direction in localStorage under this key (off by default). */
  storageKey?: string;
}

function readStorage(key: string | undefined): { theme?: ThemePreference; dir?: Dir } {
  if (!key || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { theme?: unknown; dir?: unknown };
    const out: { theme?: ThemePreference; dir?: Dir } = {};
    if (parsed.theme === 'light' || parsed.theme === 'dark' || parsed.theme === 'system')
      out.theme = parsed.theme;
    if (parsed.dir === 'ltr' || parsed.dir === 'rtl') out.dir = parsed.dir;
    return out;
  } catch {
    return {};
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/**
 * Applies the theme (`data-theme="light|dark"`) and direction (`dir="ltr|rtl"`) to the document
 * root (or `target`) and provides them to Radix primitives, which need the direction for keyboard
 * navigation in RTL.
 */
export function ThemeProvider({
  children,
  theme: themeProp,
  defaultTheme = 'system',
  onThemeChange,
  dir: dirProp,
  defaultDir = 'ltr',
  onDirChange,
  lang,
  target,
  storageKey,
}: ThemeProviderProps) {
  const [stored] = useState(() => readStorage(storageKey));
  const [theme, setTheme] = useControllableState<ThemePreference>({
    value: themeProp,
    defaultValue: stored.theme ?? defaultTheme,
    onChange: onThemeChange,
  });
  const [dir, setDir] = useControllableState<Dir>({
    value: dirProp,
    defaultValue: stored.dir ?? defaultDir,
    onChange: onDirChange,
  });
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined' || typeof window.matchMedia !== 'function')
      return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [theme]);

  const resolvedTheme: ResolvedTheme = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el =
      typeof target === 'function' ? target() : target === undefined ? document.documentElement : target;
    if (!el) return;
    el.setAttribute('data-theme', resolvedTheme);
    el.setAttribute('dir', dir);
    if (lang) el.setAttribute('lang', lang);
  }, [target, resolvedTheme, dir, lang]);

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ theme, dir }));
    } catch {
      // storage unavailable (private mode) — ignore
    }
  }, [storageKey, theme, dir]);

  const toggleTheme = useCallback(
    () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark'),
    [resolvedTheme, setTheme],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme, dir, setDir }),
    [theme, resolvedTheme, setTheme, toggleTheme, dir, setDir],
  );

  return (
    <ThemeContext.Provider value={value}>
      <RadixDirection.Provider dir={dir}>{children}</RadixDirection.Provider>
    </ThemeContext.Provider>
  );
}

/** Current theme + direction and their setters. Must be used inside <ThemeProvider>. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme() must be used inside <ThemeProvider>.');
  return ctx;
}
